import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { AuthContext } from "../../contexts/AuthContext";
import usePaiement from "../../hooks/usePaiement";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";
import "./../../assets/css/paiements/MesPaiementsPage.css";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const STEP = 8;

const STATUS_MAP = {
  1: { text: "Payé", cls: "paid" },
  2: { text: "En attente", cls: "pending" },
  3: { text: "Échoué", cls: "failed" },
  4: { text: "Gratuit", cls: "free" },
};
const statusInfo = (s) =>
  STATUS_MAP[Number(s)] || { text: "Inconnu", cls: "unknown" };

const TYPE_MAP = { 1: "Abonnement", 2: "Réabonnement", 3: "Boost" };
const typeLabel = (t) => TYPE_MAP[Number(t)] || "Type inconnu";

const METHOD_MAP = {
  1: { text: "MVola", icon: "bi-phone" },
  2: { text: "Orange Money", icon: "bi-phone" },
  3: { text: "Airtel Money", icon: "bi-phone" },
  4: { text: "Carte bancaire", icon: "bi-credit-card-2-front" },
  5: { text: "Gratuit", icon: "bi-gift" },
};
const methodInfo = (m) =>
  METHOD_MAP[Number(m)] || { text: "Méthode inconnue", icon: "bi-question-circle" };

const toNumber = (v) => {
  const n = Number(String(v ?? "0").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (v, devise = "MGA") => {
  const n = toNumber(v);
  const formatted = new Intl.NumberFormat("fr-FR").format(n);
  return devise === "MGA" ? `${formatted} Ar` : `${formatted} ${devise}`;
};

const hasDiscountInfo = (p) =>
  toNumber(p?.amountRemise) > 0 || !!String(p?.coupon || "").trim();

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: 1, label: "Payés" },
  { key: 2, label: "En attente" },
  { key: 3, label: "Échoués" },
  { key: 4, label: "Gratuits" },
];

function PaiementModal({ paiement, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const [copiedKey, setCopiedKey] = useState(null);
  const copyValue = (key, value) => {
    navigator.clipboard?.writeText(String(value));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1200);
  };

  const st = statusInfo(paiement?.status);
  const method = methodInfo(paiement?.methodPayment ?? paiement?.methodePaiement);
  const when = paiement?.date || paiement?.createdAt;

  const remise = toNumber(paiement?.amountRemise);
  const initialRaw = toNumber(paiement?.amountInitial);
  const coupon = String(paiement?.coupon || "").trim();
  const showDiscount = hasDiscountInfo(paiement);
  const initial = initialRaw > 0 ? initialRaw : toNumber(paiement?.amount) + remise;

  const references = [
    { key: "idStripe", label: "Référence carte", value: paiement?.idStripe },
    { key: "idMvola", label: "Référence MVola", value: paiement?.idMvola },
    { key: "idAirtel", label: "Référence Airtel Money", value: paiement?.idAirtel },
    { key: "airtelMoneyId", label: "ID transaction Airtel", value: paiement?.airtelMoneyId },
    { key: "idOrangeMoney", label: "Référence Orange Money", value: paiement?.idOrangeMoney },
    { key: "idTransactionOrange", label: "ID transaction Orange", value: paiement?.idTransactionOrange },
  ].filter((r) => r.value);

  return createPortal(
    <div className="payModalOverlay" onClick={onClose}>
      <div
        className="payModalSheet"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="payModalClose" onClick={onClose} aria-label="Fermer">
          <i className="bi bi-x-lg" />
        </button>

        <div className="payModalHead">
          <div className={`payMethodBadge ${st.cls}`}>
            <i className={`bi ${method.icon}`} />
          </div>
          <div>
            <div className="payModalArticle">{paiement?.idArticle?.titre || "Article"}</div>
            <div className="payModalWhen">
              {when ? dayjs(when).format("DD MMMM YYYY à HH:mm") : "Date inconnue"}
            </div>
          </div>
          <span className={`payStatusPill ${st.cls}`}>{st.text}</span>
        </div>

        <div className="payModalPerf" />

        <div className="payModalBody">
          <div className="payPriceBlock">
            {showDiscount && (
              <div className="payPriceLine">
                <span>Montant initial</span>
                <span className="strike">{formatAmount(initial, paiement?.devise)}</span>
              </div>
            )}
            {remise > 0 && (
              <div className="payPriceLine discount">
                <span>Remise</span>
                <span>- {formatAmount(remise, paiement?.devise)}</span>
              </div>
            )}
            {coupon && (
              <div className="payCouponChip">
                <i className="bi bi-tag-fill" /> Code {coupon}
              </div>
            )}
            <div className="payPriceLine total">
              <span>Montant payé</span>
              <span>{formatAmount(paiement?.amount, paiement?.devise)}</span>
            </div>
          </div>

          <div className="payInfoGrid">
            <div className="payInfo">
              <div className="payInfoLabel">Type</div>
              <div className="payInfoValue">{typeLabel(paiement?.type)}</div>
            </div>
            <div className="payInfo">
              <div className="payInfoLabel">Méthode</div>
              <div className="payInfoValue">{method.text}</div>
            </div>
            <div className="payInfo">
              <div className="payInfoLabel">Numéro source</div>
              <div className="payInfoValue">{paiement?.sourcePhoneNumber || "—"}</div>
            </div>
            <div className="payInfo">
              <div className="payInfoLabel">Devise</div>
              <div className="payInfoValue">{paiement?.devise || "MGA"}</div>
            </div>
          </div>

          {references.length > 0 && (
            <div className="payRefs">
              <div className="payRefsTitle">Références de transaction</div>
              {references.map((r) => (
                <div className="payRefRow" key={r.key}>
                  <div>
                    <div className="payInfoLabel">{r.label}</div>
                    <div className="payInfoValue mono">{r.value}</div>
                  </div>
                  <button
                    type="button"
                    className="payCopyBtn"
                    onClick={() => copyValue(r.key, r.value)}
                    aria-label="Copier"
                  >
                    <i className={`bi ${copiedKey === r.key ? "bi-check2" : "bi-clipboard"}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function MesPaiementsPage({ setAlert }) {
  const { user, isLogged } = useContext(AuthContext);
  const { getMyPaiements } = usePaiement();

  const [loading, setLoading] = useState(false);
  const [paiements, setPaiements] = useState([]);
  const [visibleCount, setVisibleCount] = useState(STEP);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isLogged || !user?._id) return;

    setLoading(true);
    getMyPaiements(user._id)
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        setPaiements(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        console.error(e);
        setAlert?.({ type: "danger", message: "Impossible de récupérer vos paiements." });
      })
      .finally(() => setLoading(false));
  }, [isLogged, user?._id]);

  const myPaiements = useMemo(() => {
    if (!user?._id) return [];
    return (paiements || [])
      .filter((p) => {
        const uid = p?.idUser?._id || p?.idUser;
        return String(uid) === String(user._id) && Number(p?.archive || 2) !== 1;
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0),
      );
  }, [paiements, user?._id]);

  const items = useMemo(() => {
    return myPaiements.map((p) => {
      const st = statusInfo(p?.status);
      const method = methodInfo(p?.methodPayment ?? p?.methodePaiement);
      const when = p?.date || p?.createdAt;
      return {
        id: p?._id,
        title: p?.idArticle?.titre || "Article",
        amountLabel: formatAmount(p?.amount, p?.devise),
        time: when ? dayjs(when).fromNow() : "Date inconnue",
        status: st,
        statusRaw: Number(p?.status),
        type: typeLabel(p?.type),
        method,
        discount: hasDiscountInfo(p),
        discountLabel:
          toNumber(p?.amountRemise) > 0
            ? `- ${formatAmount(p.amountRemise, p?.devise)}`
            : p?.coupon
              ? `Code ${p.coupon}`
              : "",
        raw: p,
      };
    });
  }, [myPaiements]);

  const counts = useMemo(() => {
    const c = { all: items.length, 1: 0, 2: 0, 3: 0, 4: 0 };
    items.forEach((it) => {
      if (c[it.statusRaw] !== undefined) c[it.statusRaw] += 1;
    });
    return c;
  }, [items]);

  const totalPaye = useMemo(
    () =>
      myPaiements
        .filter((p) => Number(p?.status) === 1)
        .reduce((sum, p) => sum + toNumber(p?.amount), 0),
    [myPaiements],
  );

  const filteredItems = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((it) => it.statusRaw === statusFilter);
  }, [items, statusFilter]);

  useEffect(() => setVisibleCount(STEP), [statusFilter]);

  const visible = filteredItems.slice(0, visibleCount);
  const remaining = filteredItems.length - visible.length;

  const breadcrumbItems = [
    { label: "Accueil", link: "/" },
    { label: "Mon compte", link: "/utilisateur/compte" },
    { label: "Mes paiements" },
  ];

  return (
    <div className="payPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="payHeaderBar">
          <div>
            <h1 className="payTitle">Mes paiements</h1>
            <p className="paySubtitle">
              {items.length} paiement{items.length > 1 ? "s" : ""} au total
            </p>
          </div>
          <div className="payStats">
            <div className="payStat">
              <span className="payStatValue">{formatAmount(totalPaye)}</span>
              <span className="payStatLabel">Total payé</span>
            </div>
            <div className="payStat">
              <span className="payStatValue">{counts[2]}</span>
              <span className="payStatLabel">En attente</span>
            </div>
          </div>
        </div>

        <div className="payFilters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`payChip ${statusFilter === f.key ? "active" : ""}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
              <span className="payChipCount">{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="payLoading">
            <span className="paySpinner" />
            Chargement de vos paiements...
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="payEmpty">
            <i className="bi bi-receipt" />
            <p>Aucun paiement dans cette catégorie.</p>
          </div>
        ) : (
          <div className="payGridTickets">
            {visible.map((p) => (
              <div
                key={p.id}
                className={`ticket status-${p.status.cls}`}
                onClick={() => setSelected(p.raw)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setSelected(p.raw)}
              >
                <div className="ticketTop">
                  <div className="ticketMethod">
                    <i className={`bi ${p.method.icon}`} />
                    <span>{p.method.text}</span>
                  </div>
                  <span className="ticketTime">{p.time}</span>
                </div>

                <div className="ticketBody">
                  <div className="ticketArticle" title={p.title}>{p.title}</div>
                  <div className="ticketType">{p.type}</div>
                  {p.discount && (
                    <div className="ticketDiscount">
                      <i className="bi bi-tag-fill" /> {p.discountLabel}
                    </div>
                  )}
                </div>

                <div className="ticketPerf" />

                <div className="ticketFooter">
                  <span className="ticketAmount">{p.amountLabel}</span>
                  <span className={`ticketStatus ${p.status.cls}`}>{p.status.text}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredItems.length > 0 && remaining > 0 && (
          <div className="payActions">
            <button
              type="button"
              className="payBtn"
              onClick={() => setVisibleCount((c) => Math.min(c + STEP, filteredItems.length))}
            >
              Voir plus ({remaining})
            </button>
          </div>
        )}
      </div>

      {selected && <PaiementModal paiement={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}